import { MembershipRoleEnum, MembershipStatusEnum } from "../../types/memberships"
import { isBeforeDays } from "../../utils"
import type { MembershipItemPermissions, MembershipResponse } from "./schemas"

export type MembershipViewModel = MembershipResponse &
  MembershipItemPermissions & {
    isDeactivated: boolean
    isOwner: boolean
    isStandardMember: boolean
    isLimitedMember: boolean
    fullname: string
    userInitials: string
    hasPendingInvitation: boolean
    hasAcceptedInvitation: boolean
    hasRejectedInvitation: boolean
    isNotInvited: boolean
    isDeactivatedBefore30Days: boolean
    reactivationDate: string | null
    canBeInvited: boolean
  }

export const makeMembershipViewModel = (membership: MembershipResponse): MembershipViewModel => {
  const { firstname, lastname } = membership
  const userInitials = getInitials(firstname, lastname)
  const isDeactivatedBefore30Days = getIsDeactivatedBefore30Days(membership.deactivationDate)
  const hasAcceptedInvitation = membership.invitationStatus === "accepted"
  const hasPendingInvitation = membership.invitationStatus === "pending"
  const hasRejectedInvitation = membership.invitationStatus === "rejected"
  const isNotInvited = membership.invitationStatus === "not_invited"
  const canBeInvited = checkCanBeInvited(membership)

  return {
    ...membership,
    isDeactivated: membership.status === MembershipStatusEnum.IN_ACTIVE,
    isOwner: membership.role === MembershipRoleEnum.orgOwner,
    isStandardMember: membership.role === MembershipRoleEnum.orgMember,
    isLimitedMember: membership.role === MembershipRoleEnum.orgLimitedMember,
    fullname: `${firstname} ${lastname}`.trim(),
    userInitials,
    hasPendingInvitation,
    hasAcceptedInvitation,
    hasRejectedInvitation,
    isNotInvited,
    canBeInvited,
    isDeactivatedBefore30Days,
    reactivationDate: getReactivationDate(membership),
  }
}

function getReactivationDate(membership: MembershipResponse): string | null {
  if (!membership.deactivationDate) return null
  const deactivationDate = new Date(membership.deactivationDate)
  const reactivationDate = new Date(deactivationDate)
  reactivationDate.setDate(reactivationDate.getDate() + 30)
  return reactivationDate.toISOString()
}

function getIsDeactivatedBefore30Days(deactivationDate: string | null): boolean {
  if (!deactivationDate) return false
  return isBeforeDays(new Date(deactivationDate), 30)
}

function getInitials(firstname: string, lastname?: string): string {
  return `${firstname[0]}${lastname ? lastname[0] : firstname[1]}`.toUpperCase()
}

function checkCanBeInvited(membership: MembershipResponse): boolean {
  if (membership.isConnectedToUser) return false // Already connected users cannot be invited
  if (membership.deactivationDate) return false // Cannot invite deactivated memberships
  return membership.invitationStatus === "not_invited" || membership.invitationStatus === "rejected"
}
